import React from "react";
import cn from "classnames";
import { FormattedMessage } from "react-intl";
import { useDispatch } from "react-redux";

import { editTask } from "../../../../../store/plan/actions";
import { post } from "../../../../../actions/general";
import { ITaskEditorProps } from "../../../../../../common/types";
import { IRhytKnockData } from "../../../../../../common/types/plan/rhytknock";
import { ISelectEvent } from "../../../../../types";
import { setItem, hasItem } from "../../../../../cookie";

import EditorBar, { FlexLeft, FlexRight, Center } from "../../../EditorBar";
import ScoreEditor from "../../../../Score";
import { Select } from "../../../../Form";
import getScore from "../../Common/getScore";
import getScale from "../../Common/getScale";
import getTempo from "../../Common/getTempo";
import ChangeScale from "../../Common/ChangeScale";
import ChangeTempo from "../../Common/ChangeTempo";
import useExerciseEditor from "../../Common/useExerciseEditor";
import { SelectWrapper } from "../../common-style";

import messages from "./messages";
import ChangeColor from "../../Common/ChangeColor";

const RhytKnockEditor = ({ taskIndex, plan, toggleInlineEditor, publicView, inlineEditorOpen }: ITaskEditorProps) => {
    const dispatch = useDispatch();
    const {
        openChanger,
        playable,
        handleChangeOpen,
        setPopupRef,
        storeScore,
        changeTempo,
        setPlayable,
        exportScore,
    } = useExerciseEditor({ plan, inlineEditorOpen, taskIndex });

    const changeAccuracy = ({ target: { value } }: ISelectEvent<number>) => {
        dispatch(editTask({ taskIndex, changingProp: "accuracy", val: value }));
        dispatch(post("rhyt-knock-accuracy", {
            planId: plan._id,
            index: taskIndex,
            accuracy: value,
        }));
    };

    const task = plan.tasks[taskIndex];
    const data = task.data as IRhytKnockData;
    const forwardProps = {
        plan,
        taskIndex,
        openChanger,
        handleChangeOpen,
        setPopupRef,
    }
    return (
        <div>
            {inlineEditorOpen && (
                <ScoreEditor
                    taskType="rhytName"
                    score={JSON.parse(JSON.stringify(getScore(plan, task) || {}))}
                    scale={getScale(plan, task)}
                    tempo={getTempo(plan, task)}
                    setScore={storeScore}
                    changeTempo={changeTempo}
                />
            )}
            {!publicView && (
                <EditorBar>
                    <FlexLeft>
                        {task?.data && <ChangeScale {...forwardProps} />}
                        <SelectWrapper>
                            <div>
                                <FormattedMessage {...messages.accuracy} />:
                            </div>
                            <Select
                                value={data.accuracy || 2}
                                noDelete
                                dark
                                name="accuracy"
                                onChange={changeAccuracy}
                                options={[
                                    {
                                        value: 1,
                                        label: <FormattedMessage {...messages.easy} />,
                                    },
                                    {
                                        value: 2,
                                        label: <FormattedMessage {...messages.normal} />,
                                    },
                                    {
                                        value: 3,
                                        label: <FormattedMessage {...messages.hard} />,
                                    },
                                ]}
                            />
                        </SelectWrapper>
                    </FlexLeft>
                    <Center
                        disabled={(inlineEditorOpen && !playable)}
                        onClick={() => {
                            toggleInlineEditor(!inlineEditorOpen);
                            setPlayable(true);
                            if (inlineEditorOpen) {
                                exportScore();
                            }
                            setItem("rhytNameDone", "1", 60 * 60 * 24 * 365, "/");
                        }}
                        className={cn(
                            !(inlineEditorOpen && !playable) && !hasItem("rhytNameDone") && !task.ref && "pulsate"
                        )}
                    >
                        {inlineEditorOpen ? (
                            <FormattedMessage {...messages.stopEdit} />
                        ) : (
                            <FormattedMessage {...messages.editScore} />
                        )}
                    </Center>
                    <FlexRight>
                        <ChangeTempo {...forwardProps} />
                        <ChangeColor
                            {...forwardProps}
                            color={task.data?.color}
                        />
                    </FlexRight>
                </EditorBar>
            )}
        </div>
    );
}

export default RhytKnockEditor;
